import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input
} from '@invent/wl-ui-kit-next/ui';
import { CheckCheck, Mail, MoreHorizontal, PanelRight, PencilLine } from 'lucide-react';
import * as React from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/design';
import { useCan } from '@/domains/auth';
import { ddApi } from '../../api/dd-api';
import { figuresApi } from '../../api/figures-api';
import { listsApi } from '../../api/lists-api';
import type { IListAssignee, IListRow } from '../../api/lists-contracts';
import type { IFigureSeries, IThresholdCondition } from '../../api/metrics-updates-contracts';
import { formatDay } from '../../components/days';
import { plainNumber } from '../../concern-predicate';
import { countOf } from '../../lists';
import { REQUESTS, seriesKey, seriesLabel } from '../../metrics-updates';
import { refusalMessage } from '../../refusal';
import { ASSIGNED_TO } from '../lists/editing';

/**
 * A Metrics updates row's work (SR-315, WF-10 steps 2–5): the request recorded — an outreach
 * attempt of purpose `metrics`, the DD log's own route — the figures an advisor emailed entered at
 * the round's as-of date (the record page's figure writes), or the figures confirmed unchanged
 * (survey-api's `confirmed` journal row). The last two set "Data as of"; survey-api's next read
 * no longer lists the row, and the page says so by name. Every press is `internal_fields.write`.
 *
 * **An amount is judged by survey-api**: plain decimal text goes as a number, anything else is
 * held with the shape wanted, never sent — `plainNumber`, the concern criteria's reading.
 */

export const WRITE_REASON = 'metrics-write-not-permitted';

/** The Lists' assignee cells of `rows`, one batch — DD calls' write; the sentence to say. */
export async function assignRows(
  rows: readonly IListRow[],
  accountId: Nullable<string>,
  accounts: readonly IListAssignee[]
): Promise<string> {
  try {
    const answer = await listsApi.edit({
      cells: rows.map((row) => ({
        participantId: row.participantId,
        survey: row.survey,
        field: ASSIGNED_TO,
        value: accountId,
        version: row.cells[ASSIGNED_TO]?.version ?? 0
      }))
    });
    const whom =
      accountId === null ? 'nobody' : (accounts.find((entry) => entry.accountId === accountId)?.email ?? 'the account chosen');
    const settled = answer.applied.length + answer.unchanged.length;
    const conflicts = answer.conflicts.length;
    return [
      settled > 0 && `${countOf(settled, 'advisor')} assigned to ${whom}.`,
      conflicts > 0 && `${countOf(conflicts, 'advisor')} assigned by a colleague since the list was read, and left as they are.`
    ]
      .filter(Boolean)
      .join(' ');
  } catch (error) {
    return refusalMessage(error, 'The assignment could not be saved.');
  }
}

/** A request for figures recorded: an outreach attempt of purpose `metrics`, by email. */
function recordRequest(participantId: string, survey: string) {
  return ddApi.recordOutreach(participantId, { survey, purpose: 'metrics', channel: 'email' });
}

/** One request recorded per row, in turn; the sentence to say. */
export async function recordRequests(rows: readonly IListRow[], survey: string): Promise<string> {
  let recorded = 0;
  const failed: string[] = [];
  for (const row of rows) {
    try {
      await recordRequest(row.participantId, survey);
      recorded += 1;
    } catch (error) {
      failed.push(refusalMessage(error, 'not recorded'));
    }
  }
  return [
    recorded > 0 && `A request recorded for ${countOf(recorded, 'advisor')}.`,
    failed.length > 0 && `${countOf(failed.length, 'request')} could not be recorded: ${failed[0]}`
  ]
    .filter(Boolean)
    .join(' ');
}

/** What a row's one button does next: ask, then take the reply in; an exception is reviewed. */
type NextStep = 'request' | 'figures' | 'review';

function nextStepOf(row: IListRow, exceptions: boolean): NextStep {
  if (exceptions) return 'review';
  return Number(row.cells[REQUESTS]?.v ?? 0) > 0 ? 'figures' : 'request';
}

/**
 * A row's work. **In the table** (`layout="row"`) one outlined button says the next step — record
 * the request where none was sent, enter the figures once one was, review an exception (which
 * opens the panel) — and "⋯" holds every press; **in the panel** (`layout="panel"`) the three are
 * buttons side by side. Every write is `internal_fields.write`.
 */
export function RowActions({
  row,
  name,
  survey,
  asOf,
  series,
  thresholdSeries,
  onDone,
  layout = 'row',
  exceptions = false,
  onReview
}: {
  row: IListRow;
  name: string;
  survey: string;
  asOf: string;
  series: readonly IFigureSeries[];
  thresholdSeries: readonly IThresholdCondition[];
  onDone: (sentence: string) => void;
  layout?: 'row' | 'panel';
  /** The row is on the exceptions list: its next step is a review. */
  exceptions?: boolean;
  /** Opens the row in the panel. */
  onReview?: () => void;
}) {
  const can = useCan();
  const permitted = can('internal_fields.write');
  const [busy, setBusy] = React.useState(false);
  const [dialog, setDialog] = React.useState<Nullable<'figures' | 'confirm'>>(null);
  const describedBy = permitted ? undefined : WRITE_REASON;

  const request = async () => {
    setBusy(true);
    try {
      await recordRequest(row.participantId, survey);
      onDone(`A request to ${name} recorded.`);
    } catch (error) {
      onDone(refusalMessage(error, `The request to ${name} could not be recorded.`));
    } finally {
      setBusy(false);
    }
  };

  const finished = (sentence: string) => {
    setDialog(null);
    onDone(sentence);
  };
  const dialogs = (
    <>
      {dialog === 'figures' && (
        <FiguresDialog
          participantId={row.participantId}
          name={name}
          survey={survey}
          asOf={asOf}
          series={thresholdSeries.length > 0 ? thresholdSeries : series}
          catalogue={series}
          onClose={() => setDialog(null)}
          onDone={finished}
        />
      )}
      {dialog === 'confirm' && (
        <ConfirmDialog
          participantId={row.participantId}
          name={name}
          survey={survey}
          asOf={asOf}
          onClose={() => setDialog(null)}
          onDone={finished}
        />
      )}
    </>
  );

  if (layout === 'panel') {
    return (
      <div className="flex flex-wrap gap-2" data-metrics-actions="panel">
        <Button type="button" size="sm" variant="outline" disabled={!permitted || busy} aria-describedby={describedBy} onClick={() => void request()}>
          <Mail className="size-4" aria-hidden />
          Record request
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={!permitted} aria-describedby={describedBy} onClick={() => setDialog('figures')}>
          <PencilLine className="size-4" aria-hidden />
          Enter figures…
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={!permitted} aria-describedby={describedBy} onClick={() => setDialog('confirm')}>
          <CheckCheck className="size-4" aria-hidden />
          Confirm unchanged…
        </Button>
        {dialogs}
      </div>
    );
  }

  const step = nextStepOf(row, exceptions);
  const primary = {
    request: { label: 'Record request', icon: Mail, act: () => void request(), write: true },
    figures: { label: 'Enter figures…', icon: PencilLine, act: () => setDialog('figures'), write: true },
    review: { label: 'Review', icon: PanelRight, act: () => onReview?.(), write: false }
  }[step];
  const PrimaryIcon = primary.icon;
  return (
    <span className="flex items-center justify-end gap-1" data-metrics-actions="row" data-next-step={step}>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-8 gap-1.5"
        disabled={(primary.write && !permitted) || busy}
        aria-describedby={primary.write ? describedBy : undefined}
        aria-label={`${primary.label.replace('…', '')}: ${name}`}
        onClick={primary.act}
      >
        <PrimaryIcon className="size-4" aria-hidden />
        {primary.label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button type="button" size="icon" variant="ghost" className="size-8" aria-label={`More for ${name}`}>
            <MoreHorizontal className="size-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem disabled={!permitted || busy} onSelect={() => void request()}>
            <Mail className="size-4" aria-hidden />
            Record a request
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!permitted} onSelect={() => setDialog('figures')}>
            <PencilLine className="size-4" aria-hidden />
            Enter figures…
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!permitted} onSelect={() => setDialog('confirm')}>
            <CheckCheck className="size-4" aria-hidden />
            Confirm unchanged…
          </DropdownMenuItem>
          {onReview && (
            <DropdownMenuItem onSelect={() => onReview()}>
              <PanelRight className="size-4" aria-hidden />
              Open details
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {dialogs}
    </span>
  );
}

/** The figures an advisor sent, one box per series, written at the round's as-of date. */
function FiguresDialog({
  participantId,
  name,
  survey,
  asOf,
  series,
  catalogue,
  onClose,
  onDone
}: {
  participantId: string;
  name: string;
  survey: string;
  asOf: string;
  series: readonly Pick<IFigureSeries, 'metric' | 'scope'>[];
  catalogue: readonly IFigureSeries[];
  onClose: () => void;
  onDone: (sentence: string) => void;
}) {
  const [typed, setTyped] = React.useState<Record<string, string>>({});
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const filled = series.filter((entry) => (typed[seriesKey(entry)] ?? '').trim() !== '');
  const held = filled.filter((entry) => plainNumber(typed[seriesKey(entry)].trim()) === null);

  const save = async () => {
    setBusy(true);
    setFailed(null);
    // One write per series: a refusal after a figure landed is said with what landed, and the
    // list read again — one figure in already takes the advisor off it.
    let saved = 0;
    try {
      for (const entry of filled) {
        await figuresApi.set(
          participantId,
          { surveyCode: survey, metric: entry.metric, scope: entry.scope, asOf },
          plainNumber(typed[seriesKey(entry)].trim()) as number
        );
        saved += 1;
      }
      onDone(`${name}’s figures as of ${formatDay(asOf)} entered — data as of ${formatDay(asOf)}, off the list.`);
    } catch (error) {
      const refused = refusalMessage(error, 'The figures could not be saved.');
      if (saved > 0) onDone(`${saved} of ${filled.length} of ${name}’s figures saved, then: ${refused}`);
      else setFailed(refused);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <form
          aria-label={`Enter ${name}’s figures`}
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <DialogHeader>
            <DialogTitle>Enter {name}’s figures</DialogTitle>
            <DialogDescription>
              As of {formatDay(asOf)}, the round’s date — what the advisor emailed back. Saving sets
              “Data as of” and takes {name} off the list.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {series.map((entry) => {
              const key = seriesKey(entry);
              const value = typed[key] ?? '';
              const bad = value.trim() !== '' && plainNumber(value.trim()) === null;
              return (
                <label key={key} className="grid gap-1 text-sm" data-figure-input={key}>
                  <span className="text-muted-foreground">{seriesLabel(catalogue, entry.metric, entry.scope)}, in dollars</span>
                  <Input
                    inputMode="decimal"
                    value={value}
                    aria-invalid={bad || undefined}
                    onChange={(event) => setTyped((now) => ({ ...now, [key]: event.target.value }))}
                  />
                  {bad && <span className="text-destructive">A number of dollars, such as 150,000,000.</span>}
                </label>
              );
            })}
            {failed && (
              <p role="alert" className="text-sm text-destructive">
                {failed}
              </p>
            )}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || filled.length === 0 || held.length > 0}>
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "Confirm unchanged", a confirmed press. */
function ConfirmDialog({
  participantId,
  name,
  survey,
  asOf,
  onClose,
  onDone
}: {
  participantId: string;
  name: string;
  survey: string;
  asOf: string;
  onClose: () => void;
  onDone: (sentence: string) => void;
}) {
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  const [busy, setBusy] = React.useState(false);
  const confirm = async () => {
    setBusy(true);
    setFailed(null);
    try {
      await figuresApi.confirm(participantId, survey, asOf);
      onDone(`${name}’s figures confirmed unchanged as of ${formatDay(asOf)} — off the list.`);
    } catch (error) {
      setFailed(refusalMessage(error, 'The confirmation could not be saved.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm {name}’s figures unchanged?</DialogTitle>
          <DialogDescription>
            The advisor updated the survey, or told us the figures have not changed. Their figures
            are recorded as checked as of {formatDay(asOf)}: “Data as of” is set and {name} leaves
            the list. No figure is changed.
          </DialogDescription>
        </DialogHeader>
        {failed && (
          <DialogBody>
            <p role="alert" className="text-sm text-destructive">
              {failed}
            </p>
          </DialogBody>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={busy} onClick={() => void confirm()}>
            {busy ? 'Confirming…' : 'Confirm unchanged'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
